import { hexToRgba } from "@/utils/pieces";
import { text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded } from "@/theme/variables";
import { Chip, ChipProps, Typography, css, styled } from "@mui/material";
import { FC } from "react";

export type TTagVariant = "bold" | "light";

export type TTagProps = {
  variant?: TTagVariant;
  color: string;
  textColor?: string;
  title: string;
  fontWeight?: number;
  minWidth?: string;
  hideDot?: boolean;
  // light variant の背景を薄め(20%)にせず、color をそのまま不透明で塗る。
  solidBg?: boolean;
} & Omit<ChipProps, "variant" | "color">;

export const Tag: FC<TTagProps> = ({ variant = "bold", color, title, textColor, fontWeight, minWidth = "76px", hideDot = false, solidBg = false, ...rest }) => {
  return (
    <StyledChip
      chipVariant={variant}
      backgroundColor={color}
      solidBg={solidBg}
      minWidth={minWidth}
      label={
        <StyledTypography>
          {variant === "light" && !hideDot && <StyledPoint background={color} />}
          <Typography variant="cap12" style={{ color: textColor, fontWeight: fontWeight }}>
            {title}
          </Typography>
        </StyledTypography>
      }
      {...rest}
    />
  );
};

const options = { shouldForwardProp: (propName: string) => !["chipVariant", "backgroundColor", "minWidth", "solidBg"].includes(propName) };

const StyledChip = styled(Chip, options)<{ chipVariant: TTagVariant; backgroundColor: string; minWidth: string; solidBg: boolean }>`
  ${({ chipVariant }) => (chipVariant === "bold" ? BoldTagCSS : LightTagCSS)};
  background-color: ${({ chipVariant, backgroundColor, solidBg }) =>
    chipVariant === "bold" || solidBg ? backgroundColor : hexToRgba(backgroundColor, 0.2)};
  min-width: ${({ minWidth }) => minWidth};
  .MuiChip-label {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }
  .MuiTypography-root {
    padding: 0;
  }
`;

const BoldTagCSS = css`
  border-radius: ${rounded.md};
  .MuiTypography-root {
    font-size: 12px;
    font-weight: bold;
    color: ${text.white};
  }
`;

const LightTagCSS = css`
  border-radius: ${rounded.lg};
  .MuiTypography-root {
    font-size: 14px;
    color: ${text.primary};
  }
`;

const StyledTypography = styled("span")`
  display: inline-flex;
  align-items: center;
  column-gap: 8px;
  ${css(typography.body14)}
`;

const StyledPoint = styled("span")<{ background: string }>`
  width: 8px;
  height: 8px;
  border-radius: ${rounded.full};
  background-color: ${({ background }) => background};
`;
